import { useMutation, useQuery } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';
import { Connector } from '@domain/connection/models/Connector';
import { ClientType } from '@domain/connection/models/Connector';
import { useFullSync } from '@features/connections/generic/hooks/useFullSync';

export const useWooCommerceConnector = () => {
    const { data: connector, isLoading, error } = useQuery<Connector | undefined>({
        queryKey: ['woocommerce-connector'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnectors();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.find(connector => connector.type === 'woocommerce');
        }
    });

    return {
        connector,
        isLoading,
        error
    };
};

export const useWooCommerceConnections = () => {
    const { data: connections, isLoading, error } = useQuery<Connection[]>({
        queryKey: ['woocommerce-connections'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnections();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.filter(connection => connection.connector.type === ClientType.WOOCOMMERCE);
        }
    });

    return {
        connections: connections || [],
        isLoading,
        error
    };
};

export const useWooCommerceSync = () => {
    const { mutateAsync: syncAllOrders } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.wooCommerceConnectorRepository.syncAllOrders(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const { mutateAsync: syncNewOrders } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.wooCommerceConnectorRepository.syncNewOrders(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const { mutateAsync: syncProducts } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.wooCommerceConnectorRepository.syncProducts(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const { triggerFullSync, isSyncing } = useFullSync('woocommerce');

    const syncFull = (connection: Connection) =>
        triggerFullSync({
            connectionId: connection.id,
            connectionName: connection.name,
            syncFn: (connectionId) => container.wooCommerceConnectorRepository.syncFull(connectionId),
        });

    return {
        syncAllOrders,
        syncNewOrders,
        syncProducts,
        syncFull,
        isSyncing,
    };
};

export const useWooCommerceInventoryPreview = (connectionId: string, page = 1) => {
    const { data: inventory, isLoading, error } = useQuery({
        queryKey: ['woocommerce-inventory-preview', connectionId, page],
        queryFn: async () => {
            const result = await container.wooCommerceConnectorRepository.fetchInventory(connectionId, page);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!connectionId
    });

    return {
        inventory: inventory || [],
        isLoading,
        error
    };
};
